import { useCallback, useEffect, useMemo, useState } from "react";

import { Accordion, Stack } from "@mantine/core";

import { IconChevronDown } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { NonCustomCategories } from "@/shared/constants/fee-schedule-categories";

import { NON_CUSTOM_CATEGORY_VALUE_SETS } from "../constants/fee-table";

import NonCustomFeeTable from "./NonCustomFeeTable";

function getCategoryContent(
  category: NonCustomCategories,
  refreshKey: number,
  onCategoryLoading: (loading: boolean) => void,
  readOnly: boolean,
) {
  const valueSetUrl = NON_CUSTOM_CATEGORY_VALUE_SETS[category];
  return (
    <NonCustomFeeTable
      readOnly={readOnly}
      refreshKey={refreshKey}
      valueSetUrl={valueSetUrl}
      onLoadingChange={onCategoryLoading}
    />
  );
}

interface NonCustomFeesProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
  readOnly?: boolean;
}

export default function NonCustomFees({
  refreshKey = 0,
  onLoadingChange,
  readOnly = false,
}: NonCustomFeesProps) {
  const [categoryLoading, setCategoryLoading] = useState<
    Partial<Record<NonCustomCategories, boolean>>
  >({});

  const handleCategoryLoading = useCallback((category: NonCustomCategories, loading: boolean) => {
    setCategoryLoading((previous) => ({ ...previous, [category]: loading }));
  }, []);

  const isNonCustomLoading = useMemo(
    () => Object.values(categoryLoading).some(Boolean),
    [categoryLoading],
  );

  useEffect(() => {
    onLoadingChange?.(isNonCustomLoading);
  }, [isNonCustomLoading, onLoadingChange]);

  useEffect(() => {
    return () => onLoadingChange?.(false);
  }, [onLoadingChange]);

  return (
    <Stack gap="md">
      {Object.values(NonCustomCategories).map((category) => (
        <Accordion
          radius="md"
          key={category}
          chevronSize={30}
          variant="separated"
          defaultValue={category}
          chevronPosition="right"
          chevron={<IconChevronDown size={30} />}
          styles={{
            item: {
              backgroundColor: "var(--mantine-color-other-white)",
              "&[data-active]": { backgroundColor: "var(--mantine-color-other-white)" },
            },
            control: {
              paddingTop: "var(--mantine-spacing-md)",
              paddingBottom: "var(--mantine-spacing-md)",
              paddingLeft: "var(--mantine-spacing-xl)",
              paddingRight: "var(--mantine-spacing-xl)",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "transparent" },
            },
            content: { padding: 0 },
          }}
        >
          <Accordion.Item value={category} bg="other-white" bd="1px solid cream.4">
            <Accordion.Control>
              <TextWrapper fw={600}>{category}</TextWrapper>
            </Accordion.Control>
            <Accordion.Panel>
              {getCategoryContent(
                category,
                refreshKey,
                (loading) => handleCategoryLoading(category, loading),
                readOnly,
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ))}
    </Stack>
  );
}
